import { SettingsRoutes } from '../../../pages/Settings/SettingsRoutes'
import { TrackRoutes } from '../../../pages/Tracks/TrackRoutes'
import { formatDate } from '../../helpers/date'
import trans from '../../helpers/trans'
import { TypedDispatch } from '../../redux/store'
import { setError } from '../../redux/tracks/tracksReducer'
import { ScheduleRoutes, UsersRoutes } from '../../routes/helpers'
import { PrivateRouteGroupRoutes } from '../../routes/routes'
import { DateFormat } from '../../types/dateFormatTypes'
import { isFetchResultSuccessful } from '../FetchResult'
import { getTrackStatusAndDateApiCall } from '../Track/TrackService'
import {
    EntityCategory,
    EntityCategoryItem,
    EventLog,
    EventLogEntityTypeResource,
    userEntityTypes,
} from './EventLog'

export const translateCategoryEnum = (key: string): EntityCategory => EntityCategory[key as keyof typeof EntityCategory]
export const translateToCategoryItemEnum = (key: string): EntityCategoryItem => EntityCategoryItem[key as keyof typeof EntityCategoryItem]

export const getEventLogEntityLink = async (log: EventLog, dispatch: TypedDispatch): Promise<string | undefined> => {
    if (log.type === EventLogEntityTypeResource.shift) {
        return `${PrivateRouteGroupRoutes.shifts()}/${ScheduleRoutes.shift(log.entityId)}`
    }

    if (log.type === EventLogEntityTypeResource.track) {
        try {
            const response = await getTrackStatusAndDateApiCall(log.entityId)

            if (! isFetchResultSuccessful(response)) {
                dispatch(setError(response.error))

                return undefined
            }

            const dateString = formatDate(new Date(response.data.checkIn), DateFormat.apiDate)

            return `${PrivateRouteGroupRoutes.tracks()}/${
                response.data.accepted
                    ? TrackRoutes.finished(log.entityId, dateString)
                    : TrackRoutes.review(log.entityId, dateString)
            }`
        } catch (error) {
            console.error('[getEventLogEntityLink]', error)
        }
    }

    if (userEntityTypes.includes(log.type)) {
        return `${PrivateRouteGroupRoutes.management()}/${UsersRoutes.user(log.entityId)}`
    }

    if (log.type === EventLogEntityTypeResource.department) {
        return `${PrivateRouteGroupRoutes.settings()}/${SettingsRoutes.groups(log.entityId)}`
    }

    return undefined
}

export const getEventLogEntityTranslation = (type: EventLogEntityTypeResource): string => {
    if (type === EventLogEntityTypeResource.shift) {
        return trans('common.shift')
    }

    if (type === EventLogEntityTypeResource.track) {
        return trans('common.track')
    }

    if (type === EventLogEntityTypeResource.mail) {
        return trans('common.email')
    }

    if (userEntityTypes.includes(type)) {
        return trans('common.user')
    }

    if (type === EventLogEntityTypeResource.department) {
        return trans('common.department')
    }

    return trans('common.entity')
}

export const getCategoryName = (category: EntityCategory, field?: string): string => {
    if (! field) {
        const isShiftPlanning = [EntityCategory.ShiftPlanning, EntityCategory.ShiftPlanningRemoval].includes(category)

        if (isShiftPlanning) {
            return 'shiftPlanning'
        }

        return ''
    }

    const shiftPlanningRegistrationIsAccepted = category === EntityCategory.ShiftPlanning && field === 'planned'

    if (shiftPlanningRegistrationIsAccepted) {
        return 'shiftPlanning'
    }

    const isShiftOrShiftPlanning = [EntityCategory.Shift, EntityCategory.ShiftPlanning].includes(category)

    if (isShiftOrShiftPlanning && field === 'start') {
        return 'startTime'
    }

    if (isShiftOrShiftPlanning && field === 'end') {
        return 'endTime'
    }

    if (category === EntityCategory.Employment && field === 'start') {
        return 'employmentStart'
    }

    if (category === EntityCategory.Employment && field === 'end') {
        return 'employmentEnd'
    }

    if (category === EntityCategory.MailEventLog) {
        return 'mailer'
    }

    if (category === EntityCategory.MailNotification) {
        return 'mailNotification'
    }

    return translateToCategoryItemEnum(field)
}
